/* eslint-disable no-else-return */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Button, CardActions, CircularProgress, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import React, { useMemo, useEffect, useReducer } from 'react';

import { useSortedData } from 'hooks/useSortedData';
import { useGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useParams } from 'react-router-dom';
import { WidgetHeader } from 'pages/WidgetManagement/common/WidgetHeader';
import CustomIcon from 'components/CustomIcon';
import { createNumArray } from 'utils/helpers';
import { HelpCenter, InfoOutlined } from '@mui/icons-material';
import { CustomTransComponent, GroupPath } from 'components';
import { useDispatch } from 'react-redux';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { useTranslation } from 'react-i18next';

const getCommandButtonClassName = (
    mode: string,
    sortedData: any,
    isLoading: boolean,
    isConfigurationLocked: any
): string =>
    !mode?.includes('view') || sortedData?.offlineAccess || isLoading || isConfigurationLocked ? 'disabled' : '';

const getCommandButtonSx = (type: string | undefined, color: string | undefined, isMultiDevice: boolean): any => ({
    ...(type === 'contained'
        ? { backgroundColor: color, '&:hover': { backgroundColor: color } }
        : { border: `2px solid ${color}`, '&:hover': { border: `2px solid ${color}` } }),
    borderRadius: '50px',
    minHeight: '36px',
    justifyContent: 'flex-start',
    ...(isMultiDevice && { minWidth: '193px' }),
});

const renderButtonStartIcon = (isLoading: boolean, iconData: any, iconColor: string): React.JSX.Element =>
    isLoading ? (
        <CircularProgress color="inherit" size={20} />
    ) : (
        <CustomIcon
            iconName={iconData?.web?.name ?? ''}
            family={iconData?.web?.family ?? ''}
            iconProps={{ sx: { color: iconColor } }}
        />
    );

const Component = ({
    widgetData,
    mode,
    commandHandler,
    widgetTypeId,
}: {
    widgetData: any;
    mode: string;
    commandHandler: any;
    widgetTypeId?: string;
}): React.JSX.Element => {
    const { sortedData } = useSortedData(widgetData ?? [], ['secondary'], 'ASC');
    const dispatch = useDispatch();
    const { t } = useTranslation();

    React.useEffect(() => {
        const timers: any[] = [];

        if (sortedData?.secondary?.length) {
            sortedData.secondary.forEach((item: any) => {
                if (item?.isLoading !== undefined && item?.isLoading) {
                    const timer = setTimeout(() => {
                        dispatch(
                            setMessageContent({
                                isOpen: true,
                                message: t('NFC:ERROR_MSG'),
                            })
                        );
                    }, 60000); // 60 second timeout

                    timers.push(timer);
                }
            });
        }

        return () => {
            timers.forEach((timer) => clearTimeout(timer));
        };
    }, [sortedData?.secondary]);

    const { deviceId } = useParams<{ deviceId: string }>();
    const { data: deviceData } = useGetDeviceByIdQuery(deviceId, { skip: !deviceId });
    const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

    useEffect(() => {
        const timeout = deviceData?.data?.configurationLockTimeout;
        if (!timeout) return;
        const delay = timeout - Date.now();
        if (delay <= 0) return;
        const timer = setTimeout(forceUpdate, delay);
        return () => clearTimeout(timer);
    }, [deviceData?.data?.configurationLockTimeout]);

    const isConfigurationLocked =
        deviceData?.data?.configurationLockTimeout && deviceData.data.configurationLockTimeout > Date.now();

    // Group commands by tagId
    const groupedCommands: Record<string, any[]> = useMemo(() => {
        if (!sortedData?.secondary?.length) return {};

        return sortedData.secondary.reduce((groups: any, item: any) => {
            const tagId = item.tagId || 'default';
            if (!groups[tagId]) {
                groups[tagId] = [];
            }
            groups[tagId].push(item);
            return groups;
        }, {});
    }, [sortedData]);

    // Render single button for single item in tag group
    const renderButtonContent = (item: any, color: string): React.JSX.Element => (
        <Stack>
            <Typography fontSize={'14px'} fontWeight={'600'} sx={{ color }}>
                {item?.displayName}
            </Typography>
            {widgetData?.multiDevice && (
                <GroupPath
                    key={item?.channelId}
                    pathName={item?.groupPath ? [...item.groupPath, item?.groupName] : []}
                    deviceName={item?.deviceName}
                    color={color}
                />
            )}
        </Stack>
    );

    const renderDropdownContent = (firstItem: any, color: string): React.JSX.Element => (
        <Stack>
            <Typography fontSize={'14px'} fontWeight={'600'} sx={{ color }}>
                {firstItem?.channelDisplayName}
            </Typography>
            {widgetData?.multiDevice && (
                <GroupPath
                    key={firstItem?.channelId}
                    pathName={firstItem?.groupPath ? [...firstItem.groupPath, firstItem?.groupName] : []}
                    deviceName={firstItem?.deviceName}
                    color={color}
                />
            )}
        </Stack>
    );

    const renderSingleButton = (item: any): React.JSX.Element => {
        const paletteConfig = item?.paletteConfig;
        const textColor = paletteConfig?.type === 'outlined' ? paletteConfig?.color : '#fff';
        return (
            <Button
                key={`${item.deviceId}-${item.channelId}`}
                className={getCommandButtonClassName(mode, sortedData, item?.isLoading, isConfigurationLocked)}
                variant={paletteConfig?.type ?? 'outlined'}
                onClick={(): void => {
                    if (isConfigurationLocked) return;
                    commandHandler?.(item);
                }}
                sx={getCommandButtonSx(paletteConfig?.type, paletteConfig?.color, widgetData?.multiDevice)}
                startIcon={renderButtonStartIcon(item?.isLoading, item?.icon, textColor)}
            >
                {renderButtonContent(item, textColor)}
            </Button>
        );
    };

    // Render dropdown for multiple items in tag group
    const renderDropdown = (tagId: string, items: any[]): React.JSX.Element => {
        const firstItem = items[0];
        const paletteConfig = firstItem?.paletteConfig;
        const textColor = paletteConfig?.type === 'outlined' ? paletteConfig?.color : '#fff';
        const isAnyLoading = items.find((item: any) => item.isLoading);
        return (
            <Button
                key={`${firstItem.deviceId}-${firstItem.tagId}`}
                className={getCommandButtonClassName(mode, sortedData, firstItem?.isLoading, isConfigurationLocked)}
                variant={paletteConfig?.type ?? 'outlined'}
                onClick={(): void => {
                    if (isConfigurationLocked) return;
                    commandHandler?.(items);
                }}
                sx={getCommandButtonSx(paletteConfig?.type, paletteConfig?.color, widgetData?.multiDevice)}
                startIcon={renderButtonStartIcon(isAnyLoading, firstItem?.icon, textColor)}
            >
                {renderDropdownContent(firstItem, textColor)}
            </Button>
        );
    };

    // Render commands based on grouping
    const renderCommands = () => {
        if (Object.keys(groupedCommands).length === 0) {
            return createNumArray(4)?.map((item: any) => (
                <IconButton key={item?.id} disabled={!mode?.includes('view')}>
                    <Stack
                        direction={'row'}
                        spacing={2}
                        alignItems={'center'}
                        borderRadius={'50px'}
                        py={1}
                        px={2}
                        sx={{ backgroundColor: item?.backgroundColor, color: item?.color }}
                        className="border-2"
                    >
                        <HelpCenter />
                        <Stack>
                            <Typography fontSize={'14px'} fontWeight={'600'}>
                                <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:COMMAND_NAME'} />
                            </Typography>
                        </Stack>
                    </Stack>
                </IconButton>
            ));
        }

        return Object.entries(groupedCommands).map(([tagId, items]: [string, any[]]) => {
            if (tagId === 'default' && items.length > 1) {
                return (
                    <React.Fragment key={tagId}>{items.map((item: any) => renderSingleButton(item))}</React.Fragment>
                );
            }

            if (items.length === 1) {
                return renderSingleButton(items[0]);
            }

            return renderDropdown(tagId, items);
        });
    };
    return (
        <Paper
            sx={{
                height: '100%',
                overflow: 'scroll',
                ...(mode?.includes('pinned-view') && { margin: '16px' }),
            }}
        >
            {((widgetData?.isPinned && !mode?.includes('pinned-view') && mode !== 'configured') ||
                widgetData?.viewTitleBar) && (
                <WidgetHeader mode={mode} widgetData={widgetData} sortedData={sortedData} widgetTypeId={widgetTypeId} />
            )}
            {!mode?.includes('edit') && <Divider />}
            <Stack
                sx={{
                    height: `calc(100% - ${mode === 'edit' || widgetData?.viewAllButton ? '97px' : '49px'})`,
                    overflow: 'auto',
                }}
            >
                <Stack
                    direction={'row'}
                    gap={2}
                    flexWrap={mode?.includes('pinned') ? 'nowrap' : 'wrap'}
                    py={mode?.includes('pinned') ? 2 : 4}
                    px={2}
                >
                    {renderCommands()}
                </Stack>
            </Stack>
            {widgetData?.isPinned && mode === 'preview' && (
                <CardActions className="padding-0 border-top-1">
                    <Stack alignItems={'center'} p={2} direction={'row'} spacing={1} className="color-content">
                        <InfoOutlined />
                        <Typography variant="body2" fontSize={'14px'}>
                            <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:PINNED_DASHBOARD'} />
                        </Typography>
                    </Stack>
                </CardActions>
            )}
        </Paper>
    );
};

export default Component;
